import React, { useRef, useState } from "react";
import { X, Upload, Image as ImageIcon, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Modal for player to upload payment proof.
 * Props: matchId, onClose, onUpdated()
 */
export default function PaymentProofModal({ matchId, onClose, onUpdated }) {
  const [preview, setPreview] = useState(null);
  const [dataUrl, setDataUrl] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("File terlalu besar (maks 4MB sebelum kompres)");
      return;
    }
    try {
      const compressed = await compressImage(file);
      setDataUrl(compressed);
      setPreview(compressed);
    } catch (e) {
      toast.error("Gagal proses gambar");
    }
  };

  const submit = async () => {
    if (!dataUrl) {
      toast.error("Upload bukti dulu");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/matches/${matchId}/payment/proof`, { proof_image: dataUrl, notes });
      toast.success("Bukti terkirim! Menunggu approval host.");
      onUpdated && (await onUpdated());
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal upload");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md bg-card border border-border rounded-t-3xl sm:rounded-3xl" onClick={(e) => e.stopPropagation()} data-testid="payment-proof-modal">
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div>
            <div className="font-heading text-lg font-black tracking-tight">Upload Bukti Bayar</div>
            <div className="text-xs text-muted-foreground">Foto / screenshot transfer kamu</div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center" data-testid="proof-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 space-y-4 pb-5">
          {preview ? (
            <div className="relative rounded-2xl overflow-hidden border border-border">
              <img src={preview} alt="proof preview" className="w-full max-h-64 object-contain bg-secondary" />
              <button
                onClick={() => { setPreview(null); setDataUrl(null); }}
                className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/70 flex items-center justify-center"
                data-testid="remove-preview"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => inputRef.current?.click()}
              data-testid="pick-file"
              className="w-full border-2 border-dashed border-border rounded-2xl py-10 flex flex-col items-center gap-2 hover:border-primary/40 active:scale-[0.99] transition-all"
            >
              <Upload className="h-7 w-7 text-primary" />
              <div className="text-sm font-bold">Pilih bukti transfer</div>
              <div className="text-xs text-muted-foreground">JPG / PNG, otomatis dikompres</div>
            </button>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="proof-file-input"
          />

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Catatan (optional)</label>
            <textarea
              data-testid="proof-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Misal: Sudah transfer dari BCA 1234"
              className="mt-2 w-full bg-secondary border border-border rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-primary resize-none"
            />
          </div>

          <button
            onClick={submit}
            disabled={submitting || !dataUrl}
            data-testid="proof-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-50 glow-cyan active:scale-95"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
            {submitting ? "Mengirim..." : "Kirim Bukti"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Resize/compress to max 1280px on long side, JPEG quality 0.8, returns data URL
async function compressImage(file) {
  const img = await fileToImage(file);
  const maxDim = 1280;
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    if (width >= height) {
      height = Math.round((height * maxDim) / width);
      width = maxDim;
    } else {
      width = Math.round((width * maxDim) / height);
      height = maxDim;
    }
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", 0.8);
}

function fileToImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
